import { FormikErrors } from 'formik';

import { SignupState } from '@/types/Signup';
import {
  emailValidationError,
  phoneValidationError
} from '@/util/form_validation_errors';
import { isValidBirthday, isValidCityName, isValidName } from '@/util/regexp';

import { StepId } from '@/components/hook/applicationSteps';

/**
 * The single-page form's values map 1:1 onto the flat `SignupState`.
 *
 * Two addresses, the same as the multi-page flow: shipping in its own section,
 * billing alongside the card, with "same as my shipping address" checked by
 * default so the common case is still one address to type. The Stripe payment
 * fields are absent — the PaymentElement is not Formik-managed and reports
 * through its own onChange.
 */
export type ApplicationFormValues = Pick<
  SignupState,
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phone'
  | 'password'
  | 'birthday'
  | 'referralCode'
  | 'instagram'
  | 'tiktok'
  | 'optinPromotion'
  | 'optinText'
  | 'address'
  | 'aptUnitSuite'
  | 'city'
  | 'state'
  | 'postalCode'
  | 'sameAsShippingAddress'
  | 'billingAddress'
  | 'billingAptUnitSuite'
  | 'billingCity'
  | 'billingState'
  | 'billingPostalCode'
  // The two consents that used to live on /apply/review. They are Formik
  // fields here, but not part of any step — they sit below all three, next to
  // Submit, so `STEP_FIELDS` deliberately omits them.
  | 'paymentAgreement'
  | 'terms'
>;

/**
 * Which fields belong to which step. Used for step-completeness checks, each
 * section's Continue, and targeting errors at the right section — not for
 * splitting the form.
 *
 * `billing` owns no Formik fields: its content is the PaymentElement, whose
 * completeness arrives separately (`paymentComplete` on `stepIsComplete`).
 */
export const STEP_FIELDS: Record<StepId, (keyof ApplicationFormValues)[]> = {
  info: ['firstName', 'lastName', 'email', 'phone', 'password', 'birthday'],
  shipping: ['address', 'city', 'state', 'postalCode'],
  // Only when the member unticks "same as my shipping address" — see
  // `fieldsForStep`.
  billing: []
};

/** Billing fields, required only when billing differs from shipping. */
export const BILLING_FIELDS: (keyof ApplicationFormValues)[] = [
  'billingAddress',
  'billingCity',
  'billingState',
  'billingPostalCode'
];

function isValidDate(date: string): boolean {
  const finalDate = new Date(date);
  const currentDate = new Date();

  return !isNaN(+finalDate) && finalDate < currentDate && isValidBirthday(date);
}

/**
 * Validation for the "My Info" step — the merge of the old step 1 (account)
 * and step 2 (personalize) validators.
 */
export async function validateInfoStep(
  values: ApplicationFormValues
): Promise<FormikErrors<ApplicationFormValues>> {
  const { email, phone, password, firstName, lastName, birthday } = values;
  const errors: FormikErrors<ApplicationFormValues> = {};

  const emailError = await emailValidationError(email);
  const phoneError = phoneValidationError(phone);

  if (emailError) {
    errors.email = emailError;
  }

  if (phoneError) {
    errors.phone = phoneError;
  }

  if (!password) {
    errors.password = 'A password is required';
  } else if (password.length < 6) {
    errors.password = 'The password field must be at least 6 characters';
  }

  if (!firstName || !isValidName(firstName)) {
    errors.firstName = 'Please provide your first name';
  }

  if (!lastName || !isValidName(lastName)) {
    errors.lastName = 'Please provide your last name';
  }

  if (!birthday || !isValidDate(birthday)) {
    errors.birthday = 'Birthday must be a valid date in the format mm/dd/yyyy';
  } else if (!isValidBirthday(birthday)) {
    errors.birthday = 'Birthday must be a valid date in the past';
  }

  return errors;
}

/** Validation for the "Shipping" step — always required, no toggle. */
export function validateShippingStep(
  values: ApplicationFormValues
): FormikErrors<ApplicationFormValues> {
  const { address, city, state, postalCode } = values;

  const errors: FormikErrors<ApplicationFormValues> = {};

  if (!address) {
    errors.address = 'Please enter a valid address';
  }

  if (!city || !isValidCityName(city)) {
    errors.city = 'Please enter a valid city';
  }

  if (!state) {
    errors.state = 'Please enter a valid state';
  }

  if (!postalCode || postalCode.length !== 5) {
    errors.postalCode = 'Please enter a valid zip code';
  }

  return errors;
}

/**
 * Validation for the billing address, which is only collected when the member
 * says it differs from shipping.
 */
export function validateBillingStep(
  values: ApplicationFormValues
): FormikErrors<ApplicationFormValues> {
  const errors: FormikErrors<ApplicationFormValues> = {};

  if (values.sameAsShippingAddress) {
    return errors;
  }

  const { billingAddress, billingCity, billingState, billingPostalCode } =
    values;

  if (!billingAddress) {
    errors.billingAddress = 'Please enter a valid address';
  }

  if (!billingCity || !isValidCityName(billingCity)) {
    errors.billingCity = 'Please enter a valid city';
  }

  if (!billingState) {
    errors.billingState = 'Please enter a valid state';
  }

  if (!billingPostalCode || billingPostalCode.length !== 5) {
    errors.billingPostalCode = 'Please enter a valid zip code';
  }

  return errors;
}

export async function validateApplication(
  values: ApplicationFormValues
): Promise<FormikErrors<ApplicationFormValues>> {
  const [infoErrors, shippingErrors, billingErrors] = [
    await validateInfoStep(values),
    validateShippingStep(values),
    validateBillingStep(values)
  ];

  return { ...infoErrors, ...shippingErrors, ...billingErrors };
}

/** Fields a given step is responsible for, accounting for the billing toggle. */
export function fieldsForStep(
  step: StepId,
  values?: ApplicationFormValues
): (keyof ApplicationFormValues)[] {
  if (step === 'billing' && values && !values.sameAsShippingAddress) {
    return [...STEP_FIELDS.billing, ...BILLING_FIELDS];
  }

  return STEP_FIELDS[step];
}

/**
 * A step is complete when every required field in its group is non-empty and
 * no validation error touches that group.
 *
 * The billing step has no fields of its own — its completeness is the
 * PaymentElement's, passed in as `paymentComplete`.
 */
export function stepIsComplete(
  step: StepId,
  values: ApplicationFormValues,
  errors: FormikErrors<ApplicationFormValues>,
  paymentComplete = false
): boolean {
  const fields = fieldsForStep(step, values);

  if (step === 'billing') {
    // The card element reports for itself; the billing address, when it is
    // being collected, still has to validate like any other field group.
    const billingAddressReady = fields.every(
      (field) => !errors[field] && String(values[field] ?? '').trim() !== ''
    );

    return paymentComplete && billingAddressReady;
  }

  const allPresent = fields.every((field) => {
    const value = values[field];
    return typeof value === 'string' ? value.trim() !== '' : !!value;
  });

  const noErrors = fields.every((field) => !errors[field]);

  return allPresent && noErrors;
}
